import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart, CartItem } from '../../context/CartContext';

const CHECKOUT_URL = 'https://opocfbvkglexckmxhwsv.supabase.co/functions/v1/create-checkout-session';
const FREE_SHIPPING_THRESHOLD = 50;
const SHIPPING_COST = 8.95;

const itemTypeIcon = (type: CartItem['type']) => {
  switch (type) {
    case 'blueprint': return 'ri-file-download-line';
    case 'consultation': return 'ri-calendar-check-line';
    case 'kit': return 'ri-fire-line';
    default: return 'ri-shopping-bag-line';
  }
};

const itemTypeLabel = (type: CartItem['type']) => {
  switch (type) {
    case 'blueprint': return 'Digitale Blueprint';
    case 'consultation': return 'Spiritueel Consult';
    case 'kit': return 'Rituele Kit';
    default: return 'Product';
  }
};

const itemTypeColor = (type: CartItem['type']) => {
  switch (type) {
    case 'blueprint': return 'text-fuchsia-400';
    case 'consultation': return 'text-violet-400';
    case 'kit': return 'text-orange-400';
    default: return 'text-violet-400';
  }
};

export const CartDrawer = () => {
  const { items, isOpen, closeCart, removeFromCart, updateQuantity, totalPrice, totalItems } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const physicalItems = items.filter(i => i.type === 'product' || i.type === 'kit');
  const showShipping = physicalItems.length > 0;
  const hasFreeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = showShipping && !hasFreeShipping ? SHIPPING_COST : 0;
  const grandTotal = totalPrice + shippingCost;
  const shippingRemaining = Math.max(0, FREE_SHIPPING_THRESHOLD - totalPrice);
  const shippingProgress = Math.min(100, (totalPrice / FREE_SHIPPING_THRESHOLD) * 100);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setIsLoading(true);
    setCheckoutError(null);
    try {
      const hasBlueprint = items.some(i => i.type === 'blueprint');
      const hasConsultation = items.some(i => i.type === 'consultation');
      const overallType = hasBlueprint ? 'blueprint' : hasConsultation ? 'consultation' : 'product';

      const checkoutItems = items.map(i => ({
        name: i.name,
        price: i.price,
        quantity: i.quantity,
        description: i.description ?? '',
        type: i.type,
      }));

      // Voeg verzendkosten toe als apart item als van toepassing
      if (showShipping && !hasFreeShipping) {
        checkoutItems.push({
          name: 'Verzendkosten',
          price: SHIPPING_COST,
          quantity: 1,
          description: 'Standaard verzending — gratis vanaf €50',
          type: 'product',
        });
      }

      const res = await fetch(CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: checkoutItems,
          type: overallType,
          metadata: {
            type: overallType,
            ...items
              .filter(i => i.type === 'consultation' && i.metadata)
              .reduce((acc, i) => ({ ...acc, ...i.metadata }), {} as Record<string, string>),
          },
          successUrl: `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/shop?status=cancelled`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError('Er is iets misgegaan. Probeer het opnieuw.');
      }
    } catch {
      setCheckoutError('Verbindingsfout. Controleer je internet en probeer opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity"
          onClick={closeCart}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#0d0020] border-l border-violet-500/15 z-50 flex flex-col transition-transform duration-400 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-violet-400 text-xl" />
            </div>
            <h2 className="text-white font-semibold text-lg">Winkelwagen</h2>
            {totalItems > 0 && (
              <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full">
                {totalItems} item{totalItems !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Free Shipping Progress */}
        {showShipping && (
          <div className={`px-5 py-3 border-b border-white/5 transition-all duration-500 ${hasFreeShipping ? 'bg-emerald-500/10' : 'bg-white/3'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  {hasFreeShipping
                    ? <i className="ri-truck-line text-emerald-400 text-sm" />
                    : <i className="ri-truck-line text-white/40 text-sm" />
                  }
                </div>
                <span className={`text-xs font-medium transition-colors duration-300 ${hasFreeShipping ? 'text-emerald-400' : 'text-white/60'}`}>
                  {hasFreeShipping
                    ? 'Gefeliciteerd! Je krijgt gratis verzending'
                    : <>&nbsp;Nog <span className="text-white font-semibold">&euro;{shippingRemaining.toFixed(2)}</span> voor gratis verzending</>
                  }
                </span>
              </div>
              {!hasFreeShipping && (
                <span className="text-white/30 text-xs">&euro;{totalPrice.toFixed(2)} / &euro;{FREE_SHIPPING_THRESHOLD}</span>
              )}
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ease-out ${hasFreeShipping ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : 'bg-gradient-to-r from-violet-500 to-fuchsia-500'}`}
                style={{ width: `${shippingProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/5">
                <i className="ri-shopping-bag-line text-3xl text-white/20" />
              </div>
              <p className="text-white/40 text-sm text-center">Je winkelwagen is leeg</p>
              <button
                onClick={closeCart}
                className="px-5 py-2.5 border border-violet-500/30 text-violet-400 text-sm rounded-full hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Verder Winkelen
              </button>
            </div>
          ) : (
            <ul className="flex flex-col gap-3 px-4">
              {items.map(item => (
                <li key={item.id} className="flex gap-4 p-4 bg-white/5 rounded-xl border border-white/5">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover object-top rounded-lg shrink-0"
                    />
                  ) : (
                    <div className={`w-16 h-16 rounded-lg shrink-0 flex items-center justify-center ${
                      item.type === 'blueprint' ? 'bg-fuchsia-900/40 border border-fuchsia-500/25' :
                      item.type === 'consultation' ? 'bg-violet-900/40 border border-violet-500/25' :
                      'bg-orange-900/40 border border-orange-500/25'
                    }`}>
                      <i className={`${itemTypeIcon(item.type)} text-2xl ${itemTypeColor(item.type)}`} />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium mb-0.5 ${itemTypeColor(item.type)}`}>
                      {itemTypeLabel(item.type)}
                    </p>
                    <p className="text-white text-sm font-medium leading-snug mb-1 line-clamp-2">
                      {item.name}
                    </p>
                    {(item.type === 'blueprint' || item.type === 'consultation') && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/20 text-fuchsia-300 text-xs mb-1 whitespace-nowrap w-fit">
                        <i className="ri-download-cloud-line text-xs" />
                        Digitaal — geen verzending
                      </span>
                    )}
                    {(item.type === 'product' || item.type === 'kit') && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs mb-1 whitespace-nowrap w-fit">
                        <i className="ri-truck-line text-xs" />
                        {hasFreeShipping ? 'Gratis verzending' : `+€${SHIPPING_COST.toFixed(2)} verzending`}
                      </span>
                    )}
                    {item.type === 'consultation' && item.metadata && (
                      <div className="text-white/35 text-xs mb-1 space-y-0.5">
                        {item.metadata.date && (
                          <p><i className="ri-calendar-line mr-1" />{item.metadata.date}{item.metadata.time ? ` · ${item.metadata.time}` : ''}</p>
                        )}
                        {item.metadata.name && (
                          <p><i className="ri-user-line mr-1" />{item.metadata.name}</p>
                        )}
                      </div>
                    )}
                    <p className={`text-sm font-semibold mb-2 ${itemTypeColor(item.type)}`}>
                      &euro;{(item.price * item.quantity).toFixed(2)}
                    </p>
                    <div className="flex items-center gap-3">
                      {item.type !== 'consultation' && (
                        <div className="flex items-center gap-2 bg-white/5 rounded-full px-2 py-1">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-6 h-6 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-xs" />
                          </button>
                          <span className="text-white text-sm w-4 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-6 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-xs" />
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-400/60 hover:text-red-400 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Subtotaal</span>
              <span className="text-white font-semibold">&euro;{totalPrice.toFixed(2)}</span>
            </div>
            {showShipping && (
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Verzending</span>
                {hasFreeShipping
                  ? <span className="text-emerald-400 text-sm font-medium flex items-center gap-1"><i className="ri-check-line" />Gratis</span>
                  : <span className="text-white/80 text-sm font-medium">&euro;{SHIPPING_COST.toFixed(2)}</span>
                }
              </div>
            )}
            <div className="h-px bg-white/10" />
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold">Totaal</span>
              <span className="text-xl font-bold text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
                &euro;{grandTotal.toFixed(2)}
              </span>
            </div>
            {checkoutError && (
              <p className="text-red-400 text-xs text-center px-2">{checkoutError}</p>
            )}
            <button
              onClick={handleCheckout}
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />
                  <span>Doorsturen naar betaling...</span>
                </>
              ) : (
                <>
                  <i className="ri-lock-line text-base" />
                  <span>Veilig Afrekenen — &euro;{grandTotal.toFixed(2)}</span>
                </>
              )}
            </button>
            <Link
              to="/shop"
              onClick={closeCart}
              className="block text-center text-white/40 text-sm hover:text-white/70 transition-colors"
            >
              Verder Winkelen
            </Link>
          </div>
        )}
      </div>
    </>
  );
};
